import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  fetchDailyQuizzes,
  deleteDailyQuiz,
} from "../../redux/slices/dailyQuizSlice";
import DailyQuizDialog from "./DailyQuizDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { useToast } from "../../components/ui/use-toast";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Calendar,
  Users,
  Trophy,
  Clock,
} from "lucide-react";
import {
  filterBarClass,
  tablePanelClass,
  textMuted,
  activeStatusBadgeClass,
} from "../../lib/quizStyles";
import { formatDate as formatDateIST } from "../../lib/utils";

const DailyQuizzes = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const dailyQuizzes = useSelector((state) => state.dailyQuiz.dailyQuizzes);
  const loading = useSelector((state) => state.dailyQuiz.loading);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    startDate: "",
    endDate: "",
  });

  const dailyQuizData = Array.isArray(dailyQuizzes) ? dailyQuizzes : [];

  useEffect(() => {
    const params = {
      page: 1,
      limit: 200,
      ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
      ...(filters.startDate ? { start_date: filters.startDate } : {}),
      ...(filters.endDate ? { end_date: filters.endDate } : {}),
      ...(filters.status === "active" ? { is_active: "true" } : {}),
      ...(filters.status === "inactive" ? { is_active: "false" } : {}),
    };
    const handle = setTimeout(() => {
      dispatch(fetchDailyQuizzes(params));
    }, filters.search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [
    dispatch,
    filters.search,
    filters.status,
    filters.startDate,
    filters.endDate,
  ]);

  const handleCreate = () => {
    setSelectedQuiz(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (quiz) => {
    setSelectedQuiz(quiz);
    setIsDialogOpen(true);
  };

  const handleDelete = async (quizId) => {
    if (window.confirm(t("confirmDelete"))) {
      try {
        await dispatch(deleteDailyQuiz(quizId)).unwrap();
        toast({
          title: t("success"),
          description: t("dailyQuizDeleted"),
        });
      } catch (error) {
        toast({
          title: t("error"),
          description: error || t("deleteFailed"),
          variant: "destructive",
        });
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const titleText = (quiz) => {
    if (typeof quiz.title === "string") return quiz.title;
    return (
      quiz.title?.en ||
      quiz.title?.ur ||
      quiz.title?.ar ||
      quiz.title?.hi ||
      ""
    );
  };

  const filteredQuizzes = dailyQuizData.filter((quiz) => {
    const q = filters.search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      titleText(quiz).toLowerCase().includes(q) ||
      (quiz.description?.en || "").toLowerCase().includes(q) ||
      (quiz.description?.ur || "").toLowerCase().includes(q);

    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "active" && quiz.is_active) ||
      (filters.status === "inactive" && !quiz.is_active);

    const quizDateStr = String(quiz.active_date || "").slice(0, 10);
    const matchesDateRange =
      (!filters.startDate || quizDateStr >= filters.startDate) &&
      (!filters.endDate || quizDateStr <= filters.endDate);

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  const formatDate = (dateString) => formatDateIST(dateString);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t("dailyQuizzes")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("manageDailyQuizzesDescription")}
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          {t("scheduleDailyQuiz")}
        </Button>
      </div>

      {/* Filters */}
      <div className={filterBarClass}>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("searchDailyQuizzes")}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            className="pl-10"
          />
        </div>

        <Select
          value={filters.status}
          onValueChange={(value) => handleFilterChange("status", value)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            <SelectItem value="active">{t("active")}</SelectItem>
            <SelectItem value="inactive">{t("inactive")}</SelectItem>
          </SelectContent>
        </Select>

        <Input
          type="date"
          placeholder={t("startDate")}
          value={filters.startDate}
          onChange={(e) => handleFilterChange("startDate", e.target.value)}
          className="w-[150px]"
        />

        <Input
          type="date"
          placeholder={t("endDate")}
          value={filters.endDate}
          onChange={(e) => handleFilterChange("endDate", e.target.value)}
          className="w-[150px]"
        />
      </div>

      {/* Table */}
      <div className={tablePanelClass}>
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">{t("loading")}</div>
        ) : filteredQuizzes.length === 0 ? (
          <div className={`p-8 text-center ${textMuted}`}>
            {t("noDailyQuizzesFound")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("date")}</TableHead>
                <TableHead>{t("title")}</TableHead>
                <TableHead>{t("questions")}</TableHead>
                <TableHead>{t("participants")}</TableHead>
                <TableHead>{t("avgScore")}</TableHead>
                <TableHead>{t("timeLimit")}</TableHead>
                <TableHead>{t("status")}</TableHead>
                <TableHead className="text-right">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuizzes.map((quiz) => (
                <TableRow key={quiz.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(quiz.active_date)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {quiz.title?.en || quiz.title || t("untitled")}
                      </div>
                      {quiz.description?.en && (
                        <div className="text-sm text-muted-foreground truncate max-w-xs">
                          {quiz.description.en}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      {quiz.question_count ?? quiz.questionCount ?? 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {quiz.total_participants || 0}
                    </div>
                  </TableCell>
                  <TableCell>
                    {quiz.avg_score ? `${quiz.avg_score}%` : t("noData")}
                  </TableCell>
                  <TableCell>
                    {quiz.total_time_limit_minutes ? (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {quiz.total_time_limit_minutes} {t("min")}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">{t("unlimited")}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={activeStatusBadgeClass(quiz.is_active)}
                    >
                      {quiz.is_active ? t("active") : t("inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(quiz)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(quiz.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialog */}
      <DailyQuizDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        dailyQuiz={selectedQuiz}
      />
    </div>
  );
};

export default DailyQuizzes;
